import React, { useRef, useState, useEffect } from "react";
import {
  Bold, Italic, Underline, ListOrdered, List, Link as LinkIcon,
  AlignLeft, AlignCenter, AlignRight, Image as ImageIcon,
  Youtube, Heading1, Heading2, Heading3, Type, Save, X
} from "lucide-react";
import { saveSectionContent } from '../modules/contentModule';
import Modal from '../modal';
import Button from '../Button';
import ImageManager from '../ImageManager';

function HtmlEditor({children, contentData = "", onChange, pageId, sectionId, extraFields, classes }) {
   const editorRef = useRef(null);
  const initialContentRef = useRef(contentData);
  const [activeCommands, setActiveCommands] = useState({});
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [modalInput, setModalInput] = useState("");
  const [modalAltInput, setModalAltInput] = useState("");
  const [modalType, setModalType] = useState("");
  const [modalHighlight, setModalHighlight] = useState(false);
  const [modalLinkText, setModalLinkText] = useState("");
  const [isDirty, setIsDirty] = useState(false); // NEW: track unsaved changes
  const selectionRef = useRef(null);
  const [modalButtonColor, setModalButtonColor] = useState("primary");
  const [bgImage, setBgImage] = useState(extraFields?.bgImg || "");
  

  useEffect(() => {
    if (editorRef.current && contentData) editorRef.current.innerHTML = contentData;
  }, [contentData]);

  useEffect(() => {
    const handleBeforeUnload = e => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = ""; // Required for Chrome
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  const exec = (command, value = null) => {
    document.execCommand(command, false, value);
    editorRef.current.focus();
    triggerChange();
  };

  const triggerChange = () => {
    if (onChange) onChange(editorRef.current.innerHTML);
    updateActiveStates();
    setIsDirty(editorRef.current.innerHTML !== initialContentRef.current);
  };

  const handleRemoveBgImage = () => {
      if (window.confirm("Remove the background image?")) {
        setBgImage(""); // clear bg image
        setIsDirty(true); // mark as unsaved changes
      }
    };

  const handleReset = () => {
  if (window.confirm("Are you sure you want to reset this section? This will remove all content.")) {
    if (editorRef.current) editorRef.current.innerHTML = "";
    setBgImage("");
    setBgImage(""); // optional: reset background image
    triggerChange();
    setIsDirty(true); // mark as dirty so the user can save
  }
};

  const updateActiveStates = () => {
    const cmds = ["bold","italic","underline","insertOrderedList","insertUnorderedList",
                  "justifyLeft","justifyCenter","justifyRight"];
    const states = {};
    cmds.forEach(cmd => {
      try { states[cmd] = document.queryCommandState(cmd); } catch { states[cmd] = false; }
    });
    setActiveCommands(states);
  };

  useEffect(() => {
    const editor = editorRef.current;
    editor.addEventListener("keyup", updateActiveStates);
    editor.addEventListener("mouseup", updateActiveStates);
    return () => {
      editor.removeEventListener("keyup", updateActiveStates);
      editor.removeEventListener("mouseup", updateActiveStates);
    };
  }, []);

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) selectionRef.current = sel.getRangeAt(0);
  };

  const restoreSelection = () => {
    const sel = window.getSelection();
    if (selectionRef.current) {
      sel.removeAllRanges();
      sel.addRange(selectionRef.current);
    }
  };

  const openModal = (type, highlight = false) => {
  saveSelection();
  setModalType(type);
  setModalHighlight(highlight);
  setModalInput("");
  setModalAltInput("");
  setModalLinkText("");
  setModalButtonColor("primary"); // default color
  setModalTitle(
  type === "link" ? "Insert Link" :
  type === "button" ? "Insert Button" :
  type === "image" ? "Insert Image" : 
  type === "youtube" ? "Insert YouTube" :
  type === "bgimage" ? "Set Background Image" :
  ""
);
  setModalOpen(true);
};

  const handleModalSubmit = () => {
    restoreSelection();
    if (!modalInput) return;

    if (modalType === "link") {
      const text = modalLinkText || modalInput;
      if (modalHighlight) {
        exec("insertHTML", `<div class="e-btn"><a href="${modalInput}" target="_blank" class="e-btn">${text}</a></div>`);
      } else {
        exec("insertHTML", `<a href="${modalInput}" target="_blank">${text}</a>`);
      }
    } else if (modalType === "image") {
      const alt = modalAltInput || "";
      exec("insertHTML", `<img class="mx-auto" src="${modalInput}" alt="${alt}" style="max-width:100%;" />`);
    } else if (modalType === "youtube") {
      const match = modalInput.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
      if (match) exec("insertHTML", `<iframe class="e-vid w-full aspect-video" src="https://www.youtube.com/embed/${match[1]}" frameborder="0" allowfullscreen></iframe>`);
      else alert("Invalid YouTube URL");
    } else if (modalType === "button") {
        const color = modalButtonColor;
        const text = modalLinkText?.trim() || modalInput; // remove extra spaces
        exec("insertHTML", `
            <a href="${modalInput}" target="_blank"
            class="px-4 py-2 rounded inline-block bg-${color} text-black hover:text-black hover:bg-${color}Dark transition font-bold no-underline w-1/2 text-center">${text}</a>
        `);
    } else if (modalType === "bgimage") {
        console.log("Setting background image:", modalInput);
        setBgImage(modalInput);
        setIsDirty(true); // ✅ mark unsaved changes
      }
    setModalOpen(false);
  };

   const handleSave = () => {
        if (editorRef.current) {
            saveSectionContent(pageId, sectionId, editorRef.current.innerHTML, {
            ...extraFields,
            bgImg: bgImage, // save bg image
            });
            initialContentRef.current = editorRef.current.innerHTML;
            setIsDirty(false);
        }
        };

    

  const handleCancel = () => {
    if (editorRef.current) {
      editorRef.current.innerHTML = initialContentRef.current;
      triggerChange();
      setIsDirty(false);
    }
  };

  const ToolbarButton = ({ active, onClick, children, type }) => {
    const base = "p-2 rounded-md hover:bg-gray-200 dark:hover:bg-gray-700 transition";
    const activeCls = active ? "bg-gray-300 dark:bg-gray-600" : "";
    const special =
      type === "save"
        ? "bg-green-600 text-white hover:bg-green-700"
        : type === "cancel"
        ? "bg-red-600 text-white hover:bg-red-700"
        : type === "reset"
        ? "bg-yellow-500 text-white hover:bg-yellow-600"
        : "";
    return (
      <button
        type="button"
        className={`${base} ${activeCls} ${special} flex items-center gap-1`}
        onMouseDown={e => { e.preventDefault(); e.stopPropagation(); onClick(); }}
      >
        {children}
      </button>
    );
  };

  return (
    <div className={`${classes} w-full mx-auto border rounded-lg`} style={bgImage ? { backgroundImage: `url(${bgImage})`, backgroundSize: "cover", backgroundPosition: "center" } : {}}>
      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 border-b p-2 bg-gray-50 ">
        <ToolbarButton active={activeCommands.bold} onClick={() => exec("bold")}><Bold size={18}/></ToolbarButton>
        <ToolbarButton active={activeCommands.italic} onClick={() => exec("italic")}><Italic size={18}/></ToolbarButton>
        <ToolbarButton active={activeCommands.underline} onClick={() => exec("underline")}><Underline size={18}/></ToolbarButton>
        <ToolbarButton onClick={() => exec("formatBlock", "<h1>")}><Heading1 size={18}/></ToolbarButton>
        <ToolbarButton onClick={() => exec("formatBlock", "<h2>")}><Heading2 size={18}/></ToolbarButton>
        <ToolbarButton onClick={() => exec("formatBlock", "<h3>")}><Heading3 size={18}/></ToolbarButton>
        <ToolbarButton onClick={() => exec("formatBlock", "<p>")}><Type size={18}/></ToolbarButton>
        <ToolbarButton active={activeCommands.insertOrderedList} onClick={() => exec("insertOrderedList")}><ListOrdered size={18}/></ToolbarButton>
        <ToolbarButton active={activeCommands.insertUnorderedList} onClick={() => exec("insertUnorderedList")}><List size={18}/></ToolbarButton>
        <ToolbarButton onClick={() => openModal("link", false)}><LinkIcon size={18}/></ToolbarButton>
        <ToolbarButton onClick={() => openModal("button", true)}>🔗 Btn</ToolbarButton>
        <ToolbarButton active={activeCommands.justifyLeft} onClick={() => exec("justifyLeft")}><AlignLeft size={18}/></ToolbarButton>
        <ToolbarButton active={activeCommands.justifyCenter} onClick={() => exec("justifyCenter")}><AlignCenter size={18}/></ToolbarButton>
        <ToolbarButton active={activeCommands.justifyRight} onClick={() => exec("justifyRight")}><AlignRight size={18}/></ToolbarButton>
        <ToolbarButton onClick={() => openModal("image")}><ImageIcon size={18}/></ToolbarButton>
        <ToolbarButton onClick={() => openModal("youtube")}><Youtube size={18}/></ToolbarButton>
        <ToolbarButton onClick={() => openModal("bgimage")}>BG image</ToolbarButton>
      </div>

      

      {/* Content Area */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        className={`p-4 min-h-full h-auto outline-none prose max-w-none`}
        onInput={triggerChange}
      />

      

      {/* Modal */}
      <Modal show={modalOpen} title={modalTitle} onClose={() => setModalOpen(false)}>
        <div className="space-y-3">
          <input
            type="text"
            placeholder={
                modalType === "image" || modalType === "bgimage" ? "Image URL" :
                modalType === "link" ? "Link URL" :
                modalType === "youtube" ? "YouTube URL" :
                modalType === "button" ? "Button URL (https://example.com)" :
                ""
            }
            value={modalInput}
            onChange={e => setModalInput(e.target.value)}
            className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:outline-none"
            />
          {modalType==="link" && (
            <input
              type="text"
              placeholder="Link Text"
              value={modalLinkText}
              onChange={e=>setModalLinkText(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:outline-none"
            />
          )}
          {(modalType==="image" || modalType==="bgimage") && (
            <div>
                {modalType === "image" && (
                    <input
                    type="text"
                    placeholder="Alt text"
                    value={modalAltInput}
                    onChange={e=>setModalAltInput(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:outline-none"
                    />
            )}

            <button type="button" onClick={handleRemoveBgImage} className="mt-2 px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600">
                Remove Background Image</button>
            
            <ImageManager imgSelector={true} onSelect={(url)=>{console.log(url);setModalInput(url)}} />
            </div>
          )}

          {modalType === "button" && (
            <div className="space-y-2">
                <input
                type="text"
                placeholder="Button Text"
                value={modalLinkText}
                onChange={e => setModalLinkText(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:outline-none"
                />

                <select
                value={modalButtonColor}
                onChange={e => setModalButtonColor(e.target.value)}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary focus:outline-none"
                >
                <option value="primary">Primary</option>
                <option value="secondary">Secondary</option>
                <option value="accent">Accent</option>
                </select>
            </div>
            )}

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={()=>setModalOpen(false)} className="px-4 py-2 rounded-lg bg-gray-300 hover:bg-gray-400">
              Cancel
            </button>
            <button type="plain" onClick={handleModalSubmit} className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700">
              Insert
            </button>
          </div>
          
        </div>
      </Modal>

       {/* Save / Cancel */}
      <div className="flex justify-end gap-2 border-t p-2 bg-white">
        <ToolbarButton type="reset" onClick={handleReset}><X size={16}/> Reset</ToolbarButton>
        {isDirty ? (
          <>
  
            <ToolbarButton type="cancel" onClick={handleCancel}><X size={16}/> Cancel</ToolbarButton>
            <ToolbarButton type="save" onClick={handleSave}><Save size={16}/> Save</ToolbarButton>
          </>
        ) : (
          <button className="px-4 py-2 rounded-lg bg-gray-300 cursor-not-allowed">
            Up to date
          </button>
        )}
      </div>

      {children}
    </div>
  );
}

export default HtmlEditor;
